import { motion } from "framer-motion";
import { MapPin, Lightning, CalendarBlank, NavigationArrow, CurrencyDollar, Package, Camera, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { fmtShootWhen, money } from "../lib/helpers";
import { Badge } from "./ui/badge";

export default function AvailableShoots({ items, role, tier, onClaim, onOpen }) {
  if (!items || items.length === 0) return null;
  const isCrew = role === "photographer" || role === "pilot";
  return (
    <div className="mb-8 border border-[#B08D57] bg-[#B08D57]/10 rounded-md p-5" data-testid="available-shoots">
      <div className="flex items-center gap-2 mb-4">
        <Lightning size={20} weight="fill" color="#B8952E" />
        <h2 className="font-bold text-[#4A0000]">
          {role === "pilot" ? "Open drone jobs — claim one" : role === "photographer" ? "Available near you — claim a shoot" : "Open shoots awaiting crew"}
        </h2>
        <span className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-[#4A0000] text-white text-xs font-bold flex items-center justify-center">{items.length}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p, i) => (
          <motion.div key={p.id} data-testid={`available-card-${p.id}`}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            className="group lux-card rounded-[5px] p-4 flex flex-col transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(74,0,0,0.10)] hover:border-[#B08D57]/50">
            <button onClick={() => onOpen(p.id)} className="text-left w-full min-w-0" data-testid={`available-open-${p.id}`}>
              <h3 className="gold-underline inline-block max-w-full font-display text-xl leading-tight truncate group-hover:text-[#4A0000] transition-colors">{p.title}</h3>
              <p className="text-xs text-[#5C5A58] flex items-center gap-1 mt-1 truncate"><MapPin size={12} weight="duotone" className="flex-shrink-0" /><span className="truncate">{p.address}{p.zip ? ` · ${p.zip}` : ""}</span></p>
            </button>
            {p.package_type && (
              <div className="flex items-center gap-1.5 mt-2" data-testid={`available-package-badge-${p.id}`}>
                <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1"><Package size={11} weight="fill" />{p.package_type}</Badge>
                {p.drone && <Badge className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">Drone</Badge>}
              </div>
            )}
            <div className="flex items-center justify-between text-sm mt-3">
              <span className="text-[#5C5A58] flex items-center gap-1 whitespace-nowrap truncate min-w-0"><CalendarBlank size={14} weight="duotone" className="shrink-0" />{fmtShootWhen(p.shoot_date)}</span>
              {p.distance != null && (
                <span className="text-xs font-mono-num text-[#8a6d0a] flex items-center gap-1 whitespace-nowrap shrink-0"><NavigationArrow size={12} weight="duotone" />{p.distance} mi</span>
              )}
            </div>
            {isCrew && p.payout != null && p.payout > 0 && (
              <div className="mt-3 flex items-center justify-between bg-[#4A0000]/5 border border-[#4A0000]/15 rounded-md px-3 py-2" data-testid={`payout-${p.id}`}>
                <span className="text-xs text-[#5C5A58] flex items-center gap-1">
                  <CurrencyDollar size={14} weight="duotone" color="#4A0000" />
                  {`You earn${tier ? ` · ${tier}` : ""}`}
                </span>
                <span className="font-mono-num font-semibold text-[#4A0000]">{money(p.payout)}</span>
              </div>
            )}
            {isCrew ? (
              <Button data-testid={`claim-${p.id}`} onClick={() => onClaim(p.id)}
                className="mt-3 bg-[#4A0000] hover:bg-[#5C1A1A] text-white w-full">Claim {role === "pilot" ? "drone job" : "shoot"}</Button>
            ) : (
              (() => {
                const needsPhotog = p.needs_photographer && !p.photographer_id;
                const needsPilot = p.needs_pilot && !p.pilot_id;
                return (
                  <div className="mt-3 flex flex-wrap items-center justify-start gap-1.5" data-testid={`needs-${p.id}`}>
                    {needsPhotog && needsPilot ? (
                      <Badge data-testid={`needs-both-${p.id}`} className="bg-[#4A0000] text-white border-[#4A0000] flex items-center gap-1"><Camera size={11} weight="fill" />Needs photographer &amp; pilot</Badge>
                    ) : (
                      <>
                        {needsPhotog && <Badge data-testid={`needs-photographer-${p.id}`} className="bg-[#4A0000]/10 text-[#4A0000] border-[#4A0000]/30 flex items-center gap-1"><Camera size={11} weight="fill" />Needs photographer</Badge>}
                        {needsPilot && <Badge data-testid={`needs-pilot-${p.id}`} className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40 flex items-center gap-1"><PaperPlaneTilt size={11} weight="fill" />Needs pilot</Badge>}
                      </>
                    )}
                    {p.photographer_name && <span className="text-[10px] text-[#5C5A58]">Photographer: {p.photographer_name}</span>}
                    {p.pilot_name && <span className="text-[10px] text-[#5C5A58]">Pilot: {p.pilot_name}</span>}
                  </div>
                );
              })()
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
